import { useEffect, useState } from "react";
import { createRoot, type Root } from "react-dom/client";

import {
  bootstrapProgress,
  type BootstrapStage,
} from "core/loading/bootstrapProgress";

import {
  BOOTSTRAP_LOADER_Z_INDEX,
  BOOTSTRAP_READY_HOLD_MS,
  BOOTSTRAP_REVEAL_MS,
  FullPageBootstrapLoader,
} from "./FullPageBootstrapLoader";

const HOST_ELEMENT_ID = "bootstrap-loader-root";

// Deliberately out of scope for this loader:
// - Telemetry/metrics: intentionally none. Global loaders commonly ship
//   without dedicated instrumentation, and crashes on this path are still
//   caught and logged by AppErrorBoundary.
// - Adaptive release-grace timing: the fixed grace below can re-flash the
//   loader when an inter-gate gap exceeds it (slow networks). Tuning it
//   needs real gap measurements first; the failure mode is the old
//   pre-singleton behavior, not worse.
// - rAF-throttling fallback for backgrounded tabs: the reveal's paint-wait
//   can stall while the tab is hidden, but rAF resumes on refocus so the
//   overlay self-heals, and the host is pointer-events: none throughout.

/** Keep the host alive across Auth → JWT → route spinner handoffs. */
const RELEASE_GRACE_MS = 150;
const TICK_INTERVAL_MS = 100;

let refCount = 0;
let root: Root | null = null;
let hostElement: HTMLElement | null = null;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;
let cancelPaintWait: (() => void) | null = null;
let exiting = false;
/** True after at least one bootstrap session reached ready and tore down. */
let completedOnce = false;
const exitListeners = new Set<(exiting: boolean) => void>();

function setExiting(next: boolean) {
  if (exiting === next) {
    return;
  }
  exiting = next;
  for (const listener of exitListeners) {
    listener(next);
  }
}

/**
 * Defers work until the browser has painted at least one frame, so the app
 * mounting behind the overlay is on screen before the reveal starts.
 */
function afterNextPaint(callback: () => void): () => void {
  if (typeof requestAnimationFrame !== "function") {
    const timer = setTimeout(callback, 0);
    return () => {
      clearTimeout(timer);
    };
  }
  let inner: number | null = null;
  const outer = requestAnimationFrame(() => {
    inner = requestAnimationFrame(callback);
  });
  return () => {
    cancelAnimationFrame(outer);
    if (inner !== null) {
      cancelAnimationFrame(inner);
    }
  };
}

function BootstrapLoaderRoot() {
  const [progress, setProgress] = useState(() =>
    bootstrapProgress.getProgress(),
  );
  const [isExiting, setIsExiting] = useState(() => exiting);

  useEffect(() => {
    return bootstrapProgress.subscribe(setProgress);
  }, []);

  useEffect(() => {
    exitListeners.add(setIsExiting);
    return () => {
      exitListeners.delete(setIsExiting);
    };
  }, []);

  return <FullPageBootstrapLoader progress={progress} exiting={isExiting} />;
}

function ensureMounted() {
  if (hostElement && root) {
    return;
  }
  const el = document.createElement("div");
  el.id = HOST_ELEMENT_ID;
  // Legacy e2e waits still look for full-page-spinner; keep that id on the
  // host so those waits cover the branded overlay, not only the Blueprint path.
  el.setAttribute("data-test", "full-page-spinner");
  // The loader itself is fixed-position, so an unstyled host would collapse to
  // a zero-size box that Playwright reports as hidden — making those waits
  // resolve while the overlay is still covering the app. Fixed positioning also
  // makes the host a stacking context, so it must carry the loader's z-index or
  // app chrome paints over the overlay.
  el.style.position = "fixed";
  el.style.inset = "0";
  el.style.zIndex = String(BOOTSTRAP_LOADER_Z_INDEX);
  el.style.pointerEvents = "none";
  document.body.appendChild(el);
  try {
    root = createRoot(el);
    root.render(<BootstrapLoaderRoot />);
  } catch (error) {
    el.remove();
    root = null;
    throw error;
  }
  hostElement = el;
  if (!tickTimer) {
    tickTimer = setInterval(() => {
      bootstrapProgress.tick();
    }, TICK_INTERVAL_MS);
  }
}

function tearDown() {
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
  if (cancelPaintWait) {
    cancelPaintWait();
    cancelPaintWait = null;
  }
  setExiting(false);
  if (tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
  }
  // If unmount throws, cleanup must still run: leaving hostElement/root
  // non-null would make ensureMounted() no-op every future acquisition,
  // permanently sticking the loader for the rest of the session.
  try {
    root?.unmount();
  } finally {
    root = null;
    hostElement?.remove();
    hostElement = null;
    completedOnce = true;
    bootstrapProgress.reset();
  }
}

export function acquireBootstrapLoader() {
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
  if (cancelPaintWait) {
    cancelPaintWait();
    cancelPaintWait = null;
  }
  // A later gate still needs the loader, so abandon any in-flight reveal
  // instead of leaving a half-blurred mark on screen.
  setExiting(false);
  // If we had started the dismiss animation during a handoff gap, rewind so
  // the bar can keep reflecting real bootstrap work — without dipping to shell.
  if (bootstrapProgress.getStage() === "ready") {
    bootstrapProgress.rewindTo("route");
  } else if (completedOnce && bootstrapProgress.getStage() === "shell") {
    // Mid-session remounts (route navigations, page fetches) should not paint
    // near-empty at the cold-start shell floor.
    bootstrapProgress.setStage("route");
  }
  // Mount before bumping the count so a throw leaves refCount at 0.
  ensureMounted();
  refCount += 1;
}

export function releaseBootstrapLoader() {
  refCount = Math.max(0, refCount - 1);
  if (refCount > 0) {
    return;
  }
  if (releaseTimer) {
    clearTimeout(releaseTimer);
  }
  // Grace window covers Auth → JWT → route spinner handoffs in the same tick.
  // Only complete the bar once we're sure nothing re-acquired.
  releaseTimer = setTimeout(() => {
    releaseTimer = null;
    if (refCount !== 0) {
      return;
    }
    bootstrapProgress.setStage("ready");
    // Hold so the fill can animate to 100% before the reveal.
    releaseTimer = setTimeout(() => {
      releaseTimer = null;
      if (refCount === 0) {
        startReveal();
      }
    }, BOOTSTRAP_READY_HOLD_MS);
  }, RELEASE_GRACE_MS);
}

/**
 * Gates render the app in place of the loader, so at release time the app has
 * not painted yet. Waiting a frame keeps the reveal from uncovering a blank
 * page and popping content in mid-animation.
 */
function startReveal() {
  cancelPaintWait = afterNextPaint(() => {
    cancelPaintWait = null;
    if (refCount !== 0) {
      return;
    }
    setExiting(true);
    releaseTimer = setTimeout(() => {
      releaseTimer = null;
      if (refCount === 0) {
        tearDown();
      }
    }, BOOTSTRAP_REVEAL_MS);
  });
}

export function setBootstrapStage(stage: BootstrapStage) {
  bootstrapProgress.setStage(stage);
}

export function getBootstrapLoaderRefCountForTests() {
  return refCount;
}

export function __resetBootstrapLoaderHostForTests() {
  refCount = 0;
  completedOnce = false;
  tearDown();
  completedOnce = false;
}

// HMR replaces this module's bindings with fresh defaults, which would orphan
// the previous instance's DOM host, React root, and timers. Use the `if`
// statement form, not optional chaining: raw Rspack (no SWC lowering) compiles
// `import.meta.webpackHot?.dispose(...)` to `undefined(...)` in production,
// while the statement form is always dead-code-eliminated.
if (import.meta.webpackHot) {
  import.meta.webpackHot.dispose(() => {
    refCount = 0;
    tearDown();
  });
}
